import type { ContenidoAyuda } from "@/ui/AyudaVista";

/**
 * Ayuda de Estadísticas: una parte por pestaña (Panorama y Resumen
 * ejecutivo). Lo que antes iba en el «?» con globito y en los tooltips de los
 * títulos de cada tarjeta y de sus números.
 *
 * `puedeEjecutivo` = admin o soporte (la pestaña Resumen ejecutivo);
 * `admin` = PDF y análisis con IA (el backend los cierra a admin).
 */
export function ayudaEstadisticas({
  puedeEjecutivo,
  admin,
}: {
  puedeEjecutivo: boolean;
  admin: boolean;
}): ContenidoAyuda {
  return {
    manual: "panel",
    intro: (
      <>
        <p>
          <b>Panorama</b>: de dónde viene el trabajo de soporte — marcas, modelos, empresas,
          categorías y fallas que más casos generan, y cuánto llevan abiertos los casos vivos.
        </p>
        {puedeEjecutivo && (
          <p>
            <b>Resumen ejecutivo</b>: los números de un periodo para dirección (casos creados y
            cerrados, tiempos de resolución, en qué se va el tiempo){admin ? ", descargable en PDF" : ""}.
          </p>
        )}
      </>
    ),
    secciones: [
      {
        titulo: "Panorama: qué muestra cada tarjeta",
        items: [
          {
            label: "¿Dónde está la pelota? (abiertos)",
            text: "De los casos abiertos, cuántos tenemos nosotros, cuántos esperan al cliente y cuántos están con el proveedor. «d prom» son los días promedio que llevan en su estado actual; «más viejo» es el caso que lleva más días así.",
          },
          {
            label: "Marcas con más soporte",
            text: "Las 10 marcas con más casos, contando abiertos y cerrados (todo el histórico).",
          },
          {
            label: "Modelos con más soporte",
            text: "Los 10 modelos con más casos (abiertos y cerrados), cada uno dentro de su marca.",
          },
          {
            label: "Empresas que más soporte piden",
            text: "Los 10 clientes con más casos, abiertos y cerrados.",
          },
          {
            label: "Casos por categoría",
            text: "Qué tipo de soporte piden los clientes, en todos los casos. «— sin clasificar —» son los casos sin categoría.",
          },
          {
            label: "Fallas recurrentes por modelo",
            text: "Los casos de categoría Falla, agrupados por modelo y tipo de falla (los 10 que más se repiten).",
          },
          {
            label: "Casos abiertos por antigüedad",
            text: "Días que llevan abiertos los casos vivos, contados desde que se crearon: el promedio, cuántos caen en cada rango y los 5 más viejos. Es distinto de la ficha de actividad de la lista de casos, que cuenta desde el último movimiento.",
          },
          {
            label: "Tocar un renglón",
            text: "Abre Casos con ese filtro puesto. Llega a la pestaña Activos; los cerrados están en Histórico (entre las dos suman el número de la tarjeta). Tocar un folio abre ese caso.",
          },
        ],
      },
      {
        titulo: "Panorama: qué significa cada número",
        items: [
          { label: "El número de la derecha", text: "Cuántos casos." },
          {
            label: <span className="font-mono text-violet-300">3🛡</span>,
            text: "De esos casos, cuántos se marcaron como garantía («¿Fue garantía?» = Sí en el caso). Solo sale si hay al menos uno.",
          },
          {
            label: <span className="font-mono text-accent-300">5📟</span>,
            text: "Cuántos equipos (IMEIs) suman esos casos: un caso puede traer varios.",
          },
          { label: "La barra", text: "El tamaño comparado con el renglón más grande de la misma tarjeta." },
        ],
      },
      ...(puedeEjecutivo
        ? [
            {
              titulo: "Resumen ejecutivo: qué hace cada botón",
              items: [
                {
                  label: "Este mes / Trimestre / Personalizado",
                  text: "El periodo del resumen. «Este mes»: del día 1 a hoy. «Trimestre»: este mes y los dos anteriores completos, hasta hoy. «Personalizado»: tú eliges las dos fechas.",
                },
                ...(admin
                  ? [
                      {
                        label: "⬇ Descargar PDF",
                        text: "Abre en otra pestaña el resumen del periodo en PDF, con el membrete de Emprenet. Si ya generaste el análisis con IA de ese periodo, también sale en el PDF.",
                      },
                      {
                        label: "✨ Generar análisis con IA / ↻ Regenerar",
                        text: "La IA redacta un análisis del periodo (situación, implicaciones y recomendaciones). Solo se hace cuando lo pides; se guarda para ese periodo y la próxima vez que lo abras ya está ahí. Regenerar lo reemplaza.",
                      },
                      {
                        label: <span className="font-mono text-accent-300">TE-…</span>,
                        text: "Un TE resaltado dentro del análisis con IA: tócalo para ir a ese caso.",
                      },
                    ]
                  : []),
              ],
            },
            {
              titulo: "Resumen ejecutivo: qué significa cada indicador",
              items: [
                {
                  label: "▲ / ▼ % vs N anteriores",
                  text: "Casos creados o cerrados comparados con el periodo inmediato anterior del mismo largo (N es cuántos hubo entonces).",
                },
                {
                  label: "Abiertos hoy",
                  text: "Los casos abiertos ahora mismo (no depende del periodo), su antigüedad promedio desde que se crearon y, si hubo cierres, en cuántos días se vaciarían al ritmo de cierre del periodo.",
                },
                {
                  label: "Resolución",
                  text: "Días promedio entre que se creó y se cerró cada caso cerrado en el periodo, con la mediana. «Desde reporte del cliente» cuenta desde la fecha en que el cliente lo reportó, solo en los casos que la tienen capturada.",
                },
                {
                  label: "Flujo · n=",
                  text: "En los casos cerrados del periodo, cuánto tiempo pasaron, en promedio, con cada quien y en cada estado (se reconstruye con la bitácora). «n» es de cuántos casos sale el promedio.",
                },
                {
                  label: (
                    <span className="font-mono">
                      <span className="text-emerald-300">95%</span> <span className="text-amber-300">80%</span>{" "}
                      <span className="text-red-300">50%</span>
                    </span>
                  ),
                  text: "Cumplimiento de SLA: qué porcentaje de casos se quedó en ese estado dentro del límite de días (≤ N d) sin pasarse. Verde: 90 % o más; ámbar: de 70 a 89 %; rojo: menos de 70 %. Entre paréntesis, dentro del límite / total.",
                },
                {
                  label: <span className="font-mono text-accent-300">5📟</span>,
                  text: "Cuántos equipos (IMEIs) suman esos casos.",
                },
                {
                  label: "· N hist.",
                  text: "En fallas recurrentes: cuántos casos de esa falla hay en todo el histórico (sale solo si es distinto del periodo).",
                },
                {
                  label: "ses.",
                  text: "En tiempo configurando equipos: cuántas veces registró ese técnico tiempo de configuración en el periodo.",
                },
              ],
            },
          ]
        : []),
    ],
  };
}
